/**
 * `creditNotes.*` — append-only documents that reverse an issued invoice.
 *
 * V1.0.x exposes the full-reversal path (`raise`, `list`). Partial-reversal
 * (operator picks lines / quantities) ships in V1.x as a separate input shape.
 */
import { z } from "zod";
import { uuid, paged, isoDate, moneyPaise } from "../_common.js";

// ── raise (full or partial reversal) ──────────────────────────────
/**
 * One row in a partial-credit-note payload — the source invoice line
 * plus the quantity being returned/credited. The server validates that
 * `qtyMilli` is positive and not greater than the source line's qty
 * (you can't credit more than was billed) and recomputes the CN line's
 * paise totals via `computeLineTax`.
 */
const partialLine = z.object({
  sourceInvoiceLineId: uuid,
  /**
   * Quantity to credit, in milli-units (qty * 1000) — same scale as
   * `invoice_lines.qty_milli`. Must be ≥ 1 and ≤ the source line's qty.
   */
  qtyMilli: z.number().int().positive(),
});

export const raiseInput = z.object({
  /** The invoice this CN reverses. Must exist + be issued/partial/paid/overdue/reversed. */
  againstInvoiceId: uuid,
  /** Mandatory — printed on the customer-facing CN; auditable. */
  reason: z.string().trim().min(3, "Reason is required.").max(500),
  /** Defaults to today on the server when omitted. */
  issuedOn: isoDate.optional(),
  /**
   * Optional partial-credit selection. Omit to keep the original V1.0
   * full-reversal behaviour (copy every invoice line at full quantity).
   *
   * When supplied, every entry must reference a distinct invoice line
   * that belongs to `againstInvoiceId`; the CN's totals are recomputed
   * from these partial rows. Empty array is rejected (a partial with
   * zero lines is meaningless — operators should hit Cancel instead).
   */
  lines: z.array(partialLine).min(1).optional(),
});

export const raiseOutput = z.object({
  id: uuid,
  docNumber: z.string(),
  totalPaise: moneyPaise,
});

// ── list ──────────────────────────────────────────────────────────
export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  invoiceId: uuid.optional(),
  customerId: uuid.optional(),
});

const listRow = z.object({
  id: uuid,
  docNumber: z.string(),
  customerId: uuid,
  customerName: z.string(),
  againstInvoiceId: uuid,
  againstInvoiceDocNumber: z.string(),
  issuedOn: isoDate,
  totalPaise: moneyPaise,
  reason: z.string(),
  createdAt: z.string().datetime(),
});

export const listOutput = paged(listRow);

// ── per-line credited totals for one invoice ──────────────────────
/**
 * Returns one row per source invoice line, with the cumulative qty +
 * paise credited across every CN raised against that line. Drives the
 * strike-through annotations on the invoice document. Returns an empty
 * array when no CNs exist for the invoice.
 */
export const lineSummaryInput = z.object({ invoiceId: uuid });

const lineSummaryRow = z.object({
  sourceInvoiceLineId: uuid,
  /** Total qty credited across all CNs against this source line. */
  qtyMilli: z.number().int().nonnegative(),
  /** Total paise credited (sum of CN line totals, including tax). */
  lineTotalPaise: moneyPaise,
});

export const lineSummaryOutput = z.object({
  invoiceId: uuid,
  lines: z.array(lineSummaryRow),
});

// ── derived TS types ──────────────────────────────────────────────
export type RaiseInput = z.infer<typeof raiseInput>;
export type RaiseOutput = z.infer<typeof raiseOutput>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type ListRow = z.infer<typeof listRow>;
export type LineSummaryInput = z.infer<typeof lineSummaryInput>;
export type LineSummaryOutput = z.infer<typeof lineSummaryOutput>;
